import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnDestroy,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { InfoscreenFeedAd, InfoscreenWeather } from '../infoscreen-module.types';
import { WeatherCardComponent } from '../weather/weather-card.component';
import { adPageSeconds, adScrollFraction } from '../scroll-speed';

type Mode = 'static' | 'scroll' | 'pages';

/** Length of the fade between two sets of ads. */
const FADE_MS = 500;

/**
 * The ad band. Ads that fit stand still, centred. Ads wider than the screen either:
 *
 * - scroll sideways in an endless loop — the row is drawn twice and moved by exactly one row's
 *   width, so the seam never shows; or
 * - with `paged`, show as many as fit and fade to the next set. The screen sets `paged` while the
 *   bookings scroll vertically: two movements at right angles are hard to read.
 *
 * A hidden copy of the whole row is always laid out, so widths are known in every mode.
 */
@Component({
  selector: 'app-ad-band',
  imports: [NgTemplateOutlet, WeatherCardComponent],
  template: `
    <ng-template #adTpl let-ad>
      @if (ad.type === 'Weather') {
        @if (weather(); as weather) {
          <app-weather-card class="ad weather" [weather]="weather" />
        }
      } @else if (ad.type === 'News') {
        <div class="ad text news">
          <div class="news-from">{{ ad.body }}</div>
          <div class="headline">{{ ad.headline }}</div>
        </div>
      } @else if (ad.type === 'Image' && ad.imageUrl) {
        <img class="ad image" [src]="ad.imageUrl" alt="" (load)="measureSoon()" />
      } @else {
        <div class="ad text" [style.background]="ad.backgroundColor || '#ffffff'" [style.color]="ad.textColor || '#1d2a44'">
          @if (ad.headline) {
            <div class="headline">{{ ad.headline }}</div>
          }
          @if (ad.body) {
            <div class="body">{{ ad.body }}</div>
          }
        </div>
      }
    </ng-template>

    <div #viewport class="viewport">
      <!-- Measuring copy: never seen, always laid out. -->
      <div #measure class="row measure" aria-hidden="true">
        @for (ad of ads(); track ad.id) {
          <ng-container [ngTemplateOutlet]="adTpl" [ngTemplateOutletContext]="{ $implicit: ad }" />
        }
      </div>

      @switch (mode()) {
        @case ('pages') {
          <div class="track">
            <div class="row" [class.fading]="fading()">
              @for (ad of currentPage(); track ad.id) {
                <ng-container [ngTemplateOutlet]="adTpl" [ngTemplateOutletContext]="{ $implicit: ad }" />
              }
            </div>
          </div>
        }
        @default {
          <div class="track" [class.scrolling]="mode() === 'scroll'" [style.--scroll-duration.s]="durationSeconds()">
            @for (copy of mode() === 'scroll' ? [0, 1] : [0]; track copy) {
              <div class="row" [attr.aria-hidden]="copy === 1 ? 'true' : null">
                @for (ad of ads(); track ad.id) {
                  <ng-container [ngTemplateOutlet]="adTpl" [ngTemplateOutletContext]="{ $implicit: ad }" />
                }
              </div>
            }
          </div>
        }
      }
    </div>
  `,
  styles: `
    @keyframes ad-scroll {
      from { transform: translateX(0); }
      to { transform: translateX(-50%); }
    }
    :host {
      display: block;
      flex-shrink: 0;
      height: calc(var(--screen-h, 100vh) * 0.17);
      background: var(--theme-band-bg, rgba(0, 0, 0, 0.25));
      border-top: 1px solid rgba(var(--ink-rgb, 255, 255, 255), 0.25);
    }
    .viewport { height: 100%; overflow: hidden; position: relative; }
    .track {
      display: flex;
      height: 100%;
      width: max-content;
      min-width: 100%;
      justify-content: center;

      &.scrolling {
        justify-content: flex-start;
        animation: ad-scroll var(--scroll-duration, 30s) linear infinite;
        will-change: transform;
      }
    }
    .row {
      display: flex;
      align-items: center;
      height: 100%;
      gap: var(--gutter-size);
      padding: calc(var(--screen-h, 100vh) * 0.015) calc(var(--gutter-size) / 2);
      box-sizing: border-box;
      transition: opacity ${FADE_MS}ms ease-in-out;

      &.fading { opacity: 0; }
    }
    .measure {
      position: absolute;
      top: 0;
      left: 0;
      width: max-content;
      visibility: hidden;
      pointer-events: none;
    }
    .ad {
      height: 100%;
      flex-shrink: 0;
      border-radius: 12px;
      box-sizing: border-box;
    }
    .image { width: auto; object-fit: contain; }
    .weather { font-size: calc(var(--screen-h, 100vh) * 0.026); }
    .news { background: rgba(0, 0, 0, 0.32); color: #ffffff; text-shadow: none; }
    .news-from {
      font-size: var(--fontsize-small);
      font-weight: 800;
      color: var(--theme-accent, #32bad4);
    }
    .text {
      min-width: calc(var(--screen-w, 100vw) * 0.28);
      max-width: calc(var(--screen-w, 100vw) * 0.45);
      padding: calc(var(--screen-h, 100vh) * 0.015) calc(var(--screen-w, 100vw) * 0.02);
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: calc(var(--screen-h, 100vh) * 0.006);
      overflow: hidden;
    }
    .headline {
      font-size: var(--fontsize-medium);
      font-weight: 700;
      line-height: 1.1;
    }
    .body {
      font-size: var(--fontsize-small);
      line-height: 1.2;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  `,
})
export class AdBandComponent implements AfterViewInit, OnDestroy {
  readonly ads = input.required<InfoscreenFeedAd[]>();
  /** 1 (slow) – 10 (fast): scroll speed, or how long each set stays when paged. */
  readonly speed = input(5);
  /** Show a set at a time instead of scrolling — while the bookings scroll the other way. */
  readonly paged = input(false);
  /** The weather, for a 'Weather' item among the ads. */
  readonly weather = input<InfoscreenWeather | null>(null);

  private readonly viewport = viewChild.required<ElementRef<HTMLElement>>('viewport');
  private readonly measureRow = viewChild.required<ElementRef<HTMLElement>>('measure');

  /** True when the whole row is wider than the band. */
  private readonly overflows = signal(false);
  readonly durationSeconds = signal(30);

  /** Ad indices per set, when paged. */
  private readonly pages = signal<number[][]>([]);
  private readonly pageIndex = signal(0);
  readonly fading = signal(false);

  readonly mode = computed<Mode>(() => {
    if (!this.overflows()) return 'static';
    return this.paged() && this.pages().length > 1 ? 'pages' : 'scroll';
  });

  readonly currentPage = computed(() => {
    const ads = this.ads();
    const page = this.pages()[this.pageIndex()] ?? [];
    return page.map((i) => ads[i]).filter(Boolean);
  });

  /** A number, not the pages: a re-measure that finds the same sets must not restart the cycle. */
  private readonly pageCount = computed(() => this.pages().length);

  private resizeObserver?: ResizeObserver;
  private pageTimer?: ReturnType<typeof setInterval>;
  private fadeTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    effect(() => {
      this.ads();
      this.speed();
      this.measureSoon();
    });

    // One set at a time while paged: fade out, show the next set, fade in.
    effect(() => {
      const paging = this.mode() === 'pages';
      const count = this.pageCount();
      const seconds = adPageSeconds(this.speed());

      this.stopPaging();
      if (untracked(this.pageIndex) >= count) this.pageIndex.set(0);
      if (!paging) return;

      this.pageTimer = setInterval(() => {
        this.fading.set(true);
        this.fadeTimer = setTimeout(() => {
          this.pageIndex.update((i) => (i + 1) % count);
          this.fading.set(false);
        }, FADE_MS);
      }, seconds * 1000);
    });
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.viewport().nativeElement);
    this.resizeObserver.observe(this.measureRow().nativeElement);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.stopPaging();
  }

  /** Images change the row's width when they arrive, so every load measures again. */
  measureSoon() {
    setTimeout(() => this.measure());
  }

  private stopPaging() {
    clearInterval(this.pageTimer);
    clearTimeout(this.fadeTimer);
    this.fading.set(false);
  }

  private measure() {
    const visible = this.viewport().nativeElement.clientWidth;
    const row = this.measureRow().nativeElement;
    const width = row.scrollWidth;

    if (!visible || width <= visible) {
      this.overflows.set(false);
      return;
    }

    this.durationSeconds.set(Math.max(8, width / (visible * adScrollFraction(this.speed()))));
    this.pages.set(pagesThatFit(row, visible));
    this.overflows.set(true);
  }
}

/**
 * Groups the measured ads into sets that each fit the band, in order. An ad wider than the band on
 * its own gets a set of its own (it is clipped, as it would be in the static row).
 */
function pagesThatFit(row: HTMLElement, available: number): number[][] {
  const style = getComputedStyle(row);
  const gap = parseFloat(style.columnGap) || 0;
  const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
  const room = available - padding;

  const pages: number[][] = [];
  let current: number[] = [];
  let used = 0;

  Array.from(row.children).forEach((child, index) => {
    const width = (child as HTMLElement).offsetWidth;
    const needed = current.length ? used + gap + width : width;

    if (current.length && needed > room) {
      pages.push(current);
      current = [index];
      used = width;
    } else {
      current.push(index);
      used = needed;
    }
  });
  if (current.length) pages.push(current);

  return pages;
}
